import { BaseService } from './base.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AppSettings } from '../app.settings';
import { ToastrService } from 'ngx-toastr';

import * as _ from 'lodash';
import { Response } from '../models/response.model';
import { CacheModel } from '../models/cache.model';
import { NavItem } from '../models/nav-item.model';
import { TimeZone } from '../models/time-zone.model';
import { Country } from '../models/country.model';
import { ShippingMethod } from '../enums/shipping-method';
import { isEmptyArray } from '../shared/utils';


@Injectable()
export class ConfigService extends BaseService {
  public timezones: Array<TimeZone>;
  public countries: Country[] = [];

  //  billing countries are used  only  to add a new credit  card
  private _billingCountries: Country[] = [];
  protected tmpCache: CacheModel;

  public isCredCardPaymentActive = false;
  public isSingleCreditCardOnly = false;

  constructor(
    protected http: HttpClient,
    protected appSettings: AppSettings,
    protected toastr: ToastrService
  ) {
    super('/config', http, appSettings, toastr);

    this.cache.persistent = true;
    this.cache.prefix = 'Config';

    this.tmpCache = new CacheModel();
    this.tmpCache.expiration = 600;
  }

  getNavItems(): Observable<NavItem[]> {
    const eTag: string = this.cache.getETag('navItems');
    const httpOptions: Object = {
      headers: new HttpHeaders({ 'If-Match': eTag }),
    };

    if (this.tmpCache.has('navItems')) {
      const result = this.cache.getItems('navItems');
      _.each(result, (item, i) => {
        result[i] = new NavItem(item);
      });
      return of(result);
    }

    return this.http
      .get<Response>(this.apiURL + '/navigation', httpOptions)
      .pipe(
        map((response) => {
          let result = response.data;

          if (result.length === 0 && response.eTag === eTag) {
            result = this.cache.getItems('navItems');
          }

          _.each(result, (item, i) => {
            result[i] = new NavItem(item);
          });

          if (response.eTag != eTag) {
            this.cache.deleteItems('navItems');
            this.cache.setItems('navItems', result);
            this.cache.setETag('navItems', response.eTag);
          }

          this.tmpCache.set('navItems', true);

          return result;
        }),
        catchError(this.handleError('ConfigService::getNavItems', []))
      );
  }

  getTimeZones(): Observable<any[]> {
    const eTag: string = this.cache.getETag('timezones');
    const httpOptions: Object = {
      headers: new HttpHeaders({ 'If-Match': eTag }),
    };

    if (this.tmpCache.has('timezones')) {
      const result = this.cache.getItems('timezones');
      _.each(result, (item, i) => {
        result[i] = new TimeZone(item);
      });
      this.timezones = result;
      return of(result);
    }

    return this.http
      .get<Response>(this.apiURL + '/timezones', httpOptions)
      .pipe(
        map((response) => {
          let result = response.data;

          if (result.length === 0 && response.eTag == eTag) {
            result = this.cache.getItems('timezones');
          }

          _.each(result, (item, i) => {
            result[i] = new TimeZone(item);
          });

          if (response.eTag != eTag) {
            this.cache.deleteItems('timezones');
            this.cache.setItems('timezones', result);
            this.cache.setETag('timezones', response.eTag);
          }

          this.timezones = result;
          this.tmpCache.set('timezones', true);

          return this.timezones;
        }),
        catchError(this.handleError('ConfigService::getTimeZones', []))
      );
  }

  getCountries(): Observable<Country[]> {
    const eTag: string = this.cache.getETag('countries', true);

    const httpOptions: Object = {
      headers: new HttpHeaders({ 'If-Match': eTag }),
    };

    if (this.tmpCache.has('countries')) {
      const result = this.cache.getItems('countries');
      this.countries = result.map((item) => new Country(item));
      return of(this.countries);
    }

    return this.http
      .get<Response>(this.apiURL + '/countries', httpOptions)
      .pipe(
        map((response) => {
          let result = response.data;

          if (result.length === 0 && response.eTag === eTag) {
            result = this.cache.getItems('countries');
          }

          this.countries = result.map((item) => new Country(item));

          if (response.eTag !== eTag) {
            this.cache.deleteItems('countries');
            this.cache.setItems('countries', this.countries);
            this.cache.setETag('countries', response.eTag);
          }

          this.tmpCache.set('countries', true);

          return this.countries;
        }),
        catchError(this.handleError('ConfigService::getCountries', []))
      );
  }

  getShippingMethods(): Observable<any[]> {
    // tmp, to be replaced by a endpoint

    return of([
      { label: ShippingMethod.Ground },
      { label: ShippingMethod.TwoDay },
      { label: ShippingMethod.NextDay },
    ]);
  }

  getConfigData(key: string): Observable<any[]> {
    const eTag: string = this.cache.getETag(key, true);
    const httpOptions: Object = {
      headers: new HttpHeaders({ 'If-Match': eTag }),
    };

    if (this.tmpCache.has(key)) {
      const result = this.cache.getItems(key);
      return of(result);
    }

    return this.http
      .get<Response>(this.apiURL + '/data/' + key, httpOptions)
      .pipe(
        map((response) => {
          let result = response.data;

          if (result.length === 0 && response.eTag == eTag) {
            result = this.cache.getItems(key);
          }

          if (response.eTag != eTag) {
            this.cache.deleteItems(key);
            this.cache.setItems(key, result);
            this.cache.setETag(key, response.eTag);
          }

          this.tmpCache.set(key, true);

          return result;
        }),
        catchError(this.handleError('ConfigService::getConfigData', []))
      );
  }

  // retrieve credit card settings (turn off/on)
  public fetchCreditCardSettings(): Observable<boolean> {
    const url = `${this.apiURL}/credit-cards`;
    this.isCredCardPaymentActive = false;

    return this.http.get<Response>(url).pipe(
      map((response) => {
        if (response.data) {
          this.isCredCardPaymentActive = response.data.enabled;
          return this.isCredCardPaymentActive;
        } else {
          return false;
        }
      }),
      catchError(this.handleError('ConfigService::credit-card', false))
    );
  }

  protected handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.log(`${operation} failed: ${error.message}`);

      return of(result as T);
    };
  }

  public getFeatureBrands(): Observable<any> {
    return this.getConfigData('brands');
  }

  // get countries  that  can be used for  credit card
  public getBillingCountries(): Observable<Country[]> {
    // if already cached
    if (!isEmptyArray(this._billingCountries)) {
      return of(this._billingCountries)
    }

    // get countries from API
    return this.http
      .get<Response>(this.apiURL + '/countries-for-credit-card')
      .pipe(
        map((response) => {

          const result = response.data;
          if (isEmptyArray(result)) {
            return [];

          }

          this._billingCountries = result.map((item) => new Country(item));
          return this._billingCountries;

        }),
        catchError(this.handleError('ConfigService::getCountries', []))
      );

  }
}
